import { useState } from 'react'
import { submitPostProposal, recoverApprovalCreate, type Post } from '../../api/hub/collaboration'
import type { Person } from '../../api/hub/ai'
import { DOMAINS, Field, MutationState, useHubMutation } from '../focus/shared'

export function PostProposal({ post, me, people, requested = false }: { post: Post; me?: Person; people: Person[]; requested?: boolean }) {
  const [expanded, setExpanded] = useState(requested)
  const [proposal, setProposal] = useState('')
  const [ownerId, setOwner] = useState('')
  const [domain, setDomain] = useState<string>(DOMAINS[0])
  const [description, setDescription] = useState('')
  const [approvalId, setApproval] = useState('')
  const [outcome, setOutcome] = useState<'occurred' | 'not_occurred'>('occurred')
  const [evidence, setEvidence] = useState('')
  const recover = useHubMutation(() => recoverApprovalCreate({ mutationKey: `approval-create:post-${post.id}`, outcome, evidence: evidence.trim() }))
  const submit = useHubMutation(() => submitPostProposal(post.id, { title: post.title, proposal: proposal.trim(), ownerId, domain, description: description.trim() }), () => undefined)
  const supported = (post.progress?.supportCount ?? post.plusOneCount ?? post.plusOnes.length) >= (post.progress?.supportTarget ?? 2)
  const own = !!me && !me.isAI && me.id === post.authorId
  if (post.tier !== 'co_create' || post.projectId || post.approvalId) return null
  if (!own) return requested ? <p className="lc-meta">只有发起人本人可在此手动填写立项单，请让发起人处理。</p> : null
  const unknown = submit.isError && /结果不明|结果未知|needs_review|幂等键已绑定|approval-create:/.test(submit.error.message)
  if (approvalId) return <p role="status">立项审批已发起，等待人工审批。<a href={`/approvals?approval=${encodeURIComponent(approvalId)}`}>查看立项审批</a></p>
  return <section className="inspiration-manual-proposal" aria-label="手动立项">
    {!supported && <p className="lc-meta">支持尚未满 2 人，你仍可本人填写立项材料并发起审批。</p>}
    <p className="lc-meta">请让你的个人代理起草立项单。没有代理或等待超过 48 小时，可在这里手动填写；也可直接手动提交。内置 AI 不替你起草。</p>
    <button aria-expanded={expanded} onClick={() => setExpanded(v => !v)}>{expanded ? '收起立项表单' : '手动填写立项单'}</button>
    {expanded && <form className="lc-form" onSubmit={e => {
      e.preventDefault();
      if (proposal.trim() && ownerId && own && !submit.isPending && !unknown) submit.mutate(undefined, { onSuccess: result => setApproval((result as { id: string }).id) })
    }}>
      <h3>立项材料</h3>
      <fieldset disabled={submit.isPending || unknown}>
        <Field label="立项提案"><textarea required maxLength={20000} value={proposal} onChange={e => setProposal(e.target.value)} placeholder="写清要解决的问题、目标、做法和验收依据" /></Field>
        <div className="lc-form-grid"><Field label="项目负责人"><select required value={ownerId} onChange={e => setOwner(e.target.value)}><option value="">请选择负责人</option>{people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <Field label="业务域"><select value={domain} onChange={e => setDomain(e.target.value)}>{DOMAINS.map(d => <option key={d}>{d}</option>)}</select></Field></div>
        <Field label="补充说明"><textarea maxLength={5000} value={description} onChange={e => setDescription(e.target.value)} /></Field>
      </fieldset>
      <p className="lc-meta">这里只发起审批。审批通过后由中台规则建项目，当前提交不会直接建项目。</p>
      <button className="lc-primary" disabled={submit.isPending || unknown || !proposal.trim() || !ownerId}>{submit.isPending ? '正在提交立项审批…' : '提交立项审批'}</button>
      <MutationState mutation={submit}/>
      {unknown && <section aria-label="立项发送结果核验">
        <p role="alert">审批发起结果待核验，已暂停重发，请先<a href="/approvals">核对审批记录</a>。恢复只核验原发送记录，不生成新审批。</p>
        <p className="lc-meta">原发送记录：approval-create:post-{post.id}</p>
        <Field label="立项核验结论"><select value={outcome} onChange={e => setOutcome(e.target.value as typeof outcome)}><option value="occurred">已发生，恢复原审批记录</option><option value="not_occurred">确认未发生，允许原请求重试</option></select></Field>
        <Field label="立项核验依据"><textarea value={evidence} onChange={e => setEvidence(e.target.value)} /></Field>
        <button type="button" disabled={!evidence.trim() || recover.isPending} onClick={() => recover.mutate(undefined, { onSuccess: result => {
          if (outcome === 'occurred') setApproval((result as { id: string }).id)
          else submit.reset()
        } })}>核验原立项请求</button>
        <p className="lc-meta">服务端会读取远端原审批核验。没有权威的未发生回执，仍不能重发。</p>
        <MutationState mutation={recover}/>
      </section>}
    </form>}
  </section>
}
